<template>
  <input
    ref="inputElement"
    v-model="quantity"
    :name="name"
    :readonly="readonly"
    :disabled="disabled"
    :min="minQuantity"
    :max="maxQuantity"
    :class="{
      'z-10 border-[color:var(--color-danger)] focus:border-[color:var(--color-danger-hover)]': !!errorMessage,
    }"
    class="border-gray-300 focus:border-gray-400 -mr-px h-11 w-full appearance-none rounded-l rounded-r-none border px-1 text-center text-base leading-9 outline-none md:w-[100px] lg:text-sm"
    type="number"
    center
    truncate
    @keydown="onKeydown"
    @keyup.enter="changeQuantity"
    @input="onQuantityChanged"
    @blur="onFocusOut"
    @click="onClick"
    @wheel.prevent
  />

  <VcTooltip v-if="errorMessage" class="!block" :x-offset="28" placement="bottom-start" strategy="fixed">
    <template #trigger>
      <div class="line-clamp-1 pt-0.5 text-xs text-[color:var(--color-danger)]">
        {{ errorMessage }}
      </div>
    </template>

    <template #content>
      <div class="text-tooltip shadow-sm-x-y w-52 rounded-sm bg-white px-3.5 py-1.5 text-xs">
        {{ errorMessage }}
      </div>
    </template>
  </VcTooltip>
</template>

<script setup lang="ts">
import { useField } from "vee-validate";
import { computed, ref, toRefs, watchEffect, shallowRef } from "vue";
import { getDynamicPropertyValue } from "@/core/utilities";
import type { Property, Product } from "@/core/api/graphql/types";

interface IEmits {
  (event: "update:modelValue", value: number): void;
}

interface IProps {
  name?: string;
  minQuantity?: number;
  maxQuantity?: number;
  disabled?: boolean;
  product: Product;
  readonly?: boolean;
  error?: boolean;
  modelValue?: number;
  properties?: Property[];
  productId?: string;
}

const emit = defineEmits<IEmits>();
const props = defineProps<IProps>();
const QUANTITY_PROPERTY = "qtyincrements_property";
const inputElement = shallowRef<HTMLInputElement>();

const quantity = ref<number | undefined>();
const defaultQuantity = computed<number>(() => getDynamicPropertyValue(props.properties, QUANTITY_PROPERTY) ?? 1);
// const { cart } = useShortCart();

// const lineItemInCart = computed(() => cart.value?.items.find((item) => item.productId === props.productId));
// const countInCart = eagerComputed<number>(() => lineItemInCart.value?.quantity ?? 0);

const disabled = computed<boolean>(() => {
  if (!props.product.availabilityData.isBuyable) {
    return true;
  } else if (props.product.sapStockStatus == 1 || props.product.sapStockStatus == 2) {
    return false;
  } else if (props.product.sapStockStatus == 0 || props.product.sapStockStatus == 3) {
    return true;
  } else if (!props.product.availabilityData?.isBuyable || !props.product.availabilityData?.isAvailable) {
    return true;
  } else {
    return (
      !props.product.availabilityData?.isAvailable ||
      !props.product.availabilityData?.isInStock ||
      !props.product.availabilityData?.isBuyable ||
      !props.product.availabilityData?.availableQuantity
    );
  }
});

const { minQuantity, maxQuantity } = toRefs(props);

const { errorMessage, setValue, validate } = useField("quantity");
const changeQuantityDebouce = ref<ReturnType<typeof setTimeout> | undefined>();

function changeQuantity() {
  if (changeQuantityDebouce.value) {
    clearTimeout(changeQuantityDebouce.value);
  }

  if (!isQuantity(quantity.value)) {
    return;
  }

  changeQuantityDebouce.value = setTimeout(() => {
    if (defaultQuantity.value > 0) {
      quantity.value = Math.ceil(quantity.value! / defaultQuantity.value) * defaultQuantity.value;
    }
    emit("update:modelValue", quantity.value!);
  }, 750);
}

async function onQuantityChanged(): Promise<void> {
  if (!isQuantity(quantity.value)) {
    return;
  }

  setValue(quantity.value);

  const { valid } = await validate();

  if (!valid) {
    return;
  }

  changeQuantity();
}

function onFocusOut() {
  if (!isQuantity(quantity.value)) {
    quantity.value = props.modelValue;
  }
}

function onKeydown(e: KeyboardEvent) {
  if (e.key.toLowerCase() === "e" || e.key === "-" || e.key === "." || e.key === ",") {
    e.preventDefault();
  }
}

function isQuantity(qty: unknown): qty is number {
  const qtyAsNumber = Number(quantity.value);
  return !isNaN(qtyAsNumber);
}

watchEffect(() => {
  quantity.value = 0;
});

function onClick() {
  inputElement.value!.select();
}
</script>
